import {
  ScrollArea,
  Table,
  Badge,
  Pagination,
  ActionIcon,
  Group,
  Tooltip,
  Text,
  Stack,
  ThemeIcon,
  LoadingOverlay,
} from "@mantine/core";
import { modals } from "@mantine/modals";

import { useUnit, useGate } from "effector-react";
import { useNavigate } from "react-router";
import { campaignEntityModel } from "../../model";
import { IconDownload, IconListDetails, IconTrash } from "@tabler/icons-react";
import { campaignDetailsEntityModel } from "../../model/details";
import { colorByStatus, CampaignStatus } from "../../types";
import { useCallback } from "react";
import styles from "./styles.module.scss";

import Session from "supertokens-auth-react/recipe/session";
import { UserRoleClaim } from "supertokens-auth-react/recipe/userroles"

export const CampaignsList = () => {
  const navigate = useNavigate();

  const claimValue = Session.useClaimValue(UserRoleClaim);
  if (claimValue.loading || !claimValue.doesSessionExist) {
    return null;
  }
  const roles = claimValue.value;
  const isSuperAdmin = (Array.isArray(roles) && roles.includes("superadmin"))

  const [list, totalPages, isLoading] = useUnit([
    campaignEntityModel.$list,
    campaignEntityModel.$totalPages,
    campaignEntityModel.$isLoading,
  ]);

  const [page, setPage] = useUnit([
    campaignEntityModel.$page,
    campaignEntityModel.setPage,
  ]);

  const { deleteCampaignById, exportToCsv } = useUnit({
    deleteCampaignById: campaignDetailsEntityModel.deleteCampaignById,
    exportToCsv: campaignDetailsEntityModel.exportToCsv,
  });

  const openDeleteModal = useCallback(
    (
      campaignId: number,
      campaignName: string,
      campaignCreatedByEmail: string
    ) => {
      modals.openConfirmModal({
        centered: true,
        closeButtonProps: {
          style: {
            alignSelf: "start",
          },
        },
        title: (
          <Text size="lg" fw={500} maw={380} lineClamp={4}>
            Delete campaign «{campaignName}»
          </Text>
        ),
        children: (
          <Text size="md">
            Are you sure you want to delete this campaign and all related
            articles?
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () =>
          deleteCampaignById({ id: campaignId, email: campaignCreatedByEmail, isSuperAdmin: isSuperAdmin }),
      });
    },
    []
  );

  useGate(campaignEntityModel.Gate);

  return (
    <ScrollArea>
      <Table highlightOnHover stickyHeader bg={"white"}>
        <Table.Thead className={styles.tableHead}>
          <Table.Tr>
            <Table.Th p="md">id</Table.Th>
            <Table.Th p="md">Name</Table.Th>
            <Table.Th p="md">Created</Table.Th>
            <Table.Th p="md">Updated</Table.Th>
            <Table.Th p="md">CreatedBy</Table.Th>
            <Table.Th p="md">Status</Table.Th>
            <Table.Th p="md">Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody w="100%" bg={"white"}>
          {!isLoading &&
            list.map((item) => (
              <Table.Tr
                key={item.id}
                onClick={() => navigate(`/campaign-details/${item.id}`)}
                style={{ cursor: "pointer" }}
                className={styles.tableRow}
              >
                <Table.Td p="md">{item.id}</Table.Td>
                <Table.Td p="md" maw={200}>
                  <Tooltip label={item.name} withArrow>
                    <Text truncate>{item.name}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td p="md">{item.createdAt}</Table.Td>
                <Table.Td p="md">{item.updatedAt}</Table.Td>
                <Table.Td p="md">{item.createdByEmail}</Table.Td>

                <Table.Td p="md">
                  <Badge color={colorByStatus[item.status]} variant="light">
                    {item.status === CampaignStatus.completed && "Finished"}
                    {item.status === CampaignStatus.pending && "Processing"}
                    {item.status === CampaignStatus.failed && "Failed"}
                  </Badge>
                </Table.Td>
                <Table.Td p="md" onClick={(e) => e.stopPropagation()}>
                  <Group wrap="nowrap">
                    <Tooltip label={"Delete campaign and all related articles"}>
                      <ActionIcon
                        variant="transparent"
                        size={"sm"}
                        onClick={() =>
                          openDeleteModal(
                            Number(item.id),
                            item.name,
                            item.createdByEmail
                          )
                        }
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip
                      label={
                        item.status !== CampaignStatus.completed
                          ? "Export is available only for completed campaigns"
                          : "Export campaign report to CSV"
                      }
                    >
                      <ActionIcon
                        disabled={item.status !== CampaignStatus.completed}
                        variant="transparent"
                        size={"sm"}
                        onClick={() =>
                          exportToCsv({
                            campaignId: Number(item.id),
                          })
                        }
                      >
                        <IconDownload />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}

          {!isLoading && list.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6} p="md" bg="white" w="100%">
                <Stack
                  align="center"
                  justify="center"
                  h="200px"
                  flex={1}
                  w={"100vw"}
                >
                  <ThemeIcon size={52} radius="sm" variant="light" color="blue">
                    <IconListDetails />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    No campaigns found yet
                  </Text>
                  <Text size="sm" c="dimmed">
                    The campaigns will be displayed here after the creation.
                  </Text>
                </Stack>
              </Table.Td>
            </Table.Tr>
          )}

          {isLoading && (
            <Stack flex={1} mih={200}>
              <Stack
                mih={200}
                pos={"absolute"}
                top={0}
                left={0}
                right={0}
                bottom={0}
              >
                <LoadingOverlay
                  visible={isLoading}
                  zIndex={100}
                  overlayProps={{
                    radius: "sm",
                    blur: 5,
                    backgroundOpacity: 0.8,
                  }}
                  loaderProps={{ type: "bars" }}
                />
              </Stack>
            </Stack>
          )}
        </Table.Tbody>

        <Table.Tfoot className={styles.tableFooter}>
          <Table.Tr>
            <Table.Td colSpan={3}>
              <Pagination
                total={totalPages}
                siblings={1}
                value={page}
                onChange={setPage}
              />
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </ScrollArea>
  );
};
